import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap } from 'lucide-react';

export const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const features = [
    'Unlimited orders per month',
    'Digital menu with photos & categories',
    'AI order taking with intelligent upselling',
    'Real-time order dashboard',
    'Payment integration ready',
    'Analytics & performance reports',
    'WhatsApp priority support',
    'Free menu setup assistance',
    '24/7 AI service - never miss an order',
    'Multi-language support',
    'Custom AI training for your menu',
    'Regular updates & improvements'
  ];

  return (
    <section className="py-20 md:py-32 px-6 md:px-12" data-testid="pricing-section">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-primary bg-primary/10 px-4 py-2 rounded-full inline-block mb-4">
            Cheaper Than Hiring ONE Person
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            One plan. All features. No hidden fees. Cancel anytime.
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex justify-center mb-12"
        >
          <div className="inline-flex items-center bg-secondary/5 p-2 rounded-full border border-border/50">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-8 py-3 rounded-full font-semibold transition-all ${billingCycle === 'monthly'
                  ? 'bg-white text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
              data-testid="toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-8 py-3 rounded-full font-semibold transition-all relative ${billingCycle === 'annual'
                  ? 'bg-white text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
              data-testid="toggle-annual"
            >
              Annual
              <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-bold">
                Save 33%
              </span>
            </button>
          </div>
        </motion.div>

        {/* Single Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-2xl mx-auto"
        >
          <div className="relative bg-white p-10 md:p-12 rounded-3xl border-2 border-primary/30 shadow-2xl overflow-hidden">
            <div className="absolute top-0 right-0">
              <div className="bg-primary text-primary-foreground px-6 py-2 rounded-bl-2xl text-sm font-bold">
                BEST VALUE
              </div>
            </div>

            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                EasyOrder Pro
              </h3>
              <p className="text-muted-foreground mb-8">
                Everything you need to automate and scale your delivery business
              </p>

              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="text-6xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {billingCycle === 'monthly' ? '$99' : '$66'}
                </span>
                <span className="text-2xl text-muted-foreground">/month</span>
              </div>

              {billingCycle === 'annual' && (
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
                  <Zap className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold text-primary">
                    Billed $800 annually - Save $396/year!
                  </p>
                </div>
              )}

              {billingCycle === 'monthly' && (
                <p className="text-sm text-muted-foreground">
                  Billed monthly - switch to annual and save $396/year
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {features.map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <a
              href="https://app.easyorder.delivery/informacoes"
              data-testid="cta-start-trial"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-5 rounded-full text-xl font-semibold shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center text-center"
            >
              Start 30-Day Free Trial
            </a>

            <p className="text-center text-sm text-muted-foreground mt-4">
              No credit card required • Cancel anytime • Full access during trial
            </p>
          </div>
        </motion.div>

        {/* ROI Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16 text-center bg-accent/20 p-8 rounded-2xl max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            💡 <span className="text-primary">Reality Check:</span> ROI in 24 Hours
          </h3>
          <p className="text-lg text-muted-foreground mb-6">
            One missed lunch rush = $200 lost. EasyOrder pays for itself by catching orders you'd normally miss.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl">
              <p className="text-3xl font-bold text-foreground mb-2">
                {billingCycle === 'monthly' ? '$99' : '$66'}/mo
              </p>
              <p className="text-sm text-muted-foreground">Your investment</p>
            </div>
            <div className="bg-white p-6 rounded-xl">
              <p className="text-3xl font-bold text-foreground mb-2">$0</p>
              <p className="text-sm text-muted-foreground">Staff salaries saved</p>
            </div>
            <div className="bg-primary/10 p-6 rounded-xl border-2 border-primary">
              <p className="text-3xl font-bold text-primary mb-2">+$4,200</p>
              <p className="text-sm text-foreground font-medium">Avg. extra revenue/month</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;